import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import {
  getMeterReaderList,
  getPhotoMeterRules,
  getReadingsList,
  getValidationCompletionDetail,
  markCycleAsComplete,
  mxApi,
  runValidations,
} from "./api";
import {
  CreateTemplateOperationsPayload,
  UpdateMeterDispatchPayload,
} from "./types";
import { useCallback, useState } from "react";
import { useToast } from "@shared/ui/use-toast";
import { downloadCSV } from "@shared/utils/utils";
import { queryClient } from "@shared/api/queries/queryClient";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

export const useMeterRun = (params) => {
  return useSmartQuery(QueryKeyFactory.module.mx.meter.meterRun(params), () =>
    mxApi.getMeterRun(params)
  );
};

export const useGetCompletedMeterRun = (params) => {
  return useSmartQuery(QueryKeyFactory.module.mx.meter.meterRun(params), () =>
    mxApi.getMeterRun(params)
  );
};

export const useMeterRunExport = (params: {
  remote_utility_id: any;
  meter_type: string;
  run_status?: string;
  month_year?: string;
  ordering?: string;
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      // Remove page and page_size for export to get all data
      const exportParams = {
        ...params,
        // Don't include page/page_size to export all data
      };

      const blob = await mxApi.exportMeterRun(exportParams);
      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Failed to export manual meter reads: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [params, isExporting, toast]);

  return { exportCSV, isExporting };
};

export const useMeterRead = (params: {
  remote_utility_id: any;
  meter_type: any;
  page: number;
  run_status: any;
  month_year: any;
}) => {
  return useSmartQuery(QueryKeyFactory.module.mx.meter.meterRead(params), () =>
    mxApi.getMeterRead(params)
  );
};

export const useMeterRunSummary = (params: {
  remote_utility_id: any;
  meter_type: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meter.meterRunSummary(params),
    () => mxApi.getMeterRunSummary(params)
  );
};

export const useMeterRunDetail = (params: {
  meter_run_id: any;
  remote_utility_id: any;
  meter_type: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meter.meterRunDetails(params),
    () => mxApi.getMeterRunDetail(params)
  );
};

export const useMeterDispatchDetail = (params: {
  meter_run_id: any;
  remote_utility_id: any;
  page: number;
  origin: string;
  meter_reader_id?: any;
  run_status?: string;
  search?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meter.meterDispatch(params), // Include all params in query key for caching
    () => mxApi.getMeterDispatchDetail(params)
  );
};

export const useAssignConsumer = (params: {
  remote_utility_id: any;
  isActive: string;
  page: number;
  search?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meter.assignConsumer(params),
    () => mxApi.getAssignConsumer(params),
    {
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );
};
export const useUpdateMeterDispatchDetail = () => {
  return useSmartMutation(
    ({
      dispatchId,
      payload,
    }: {
      dispatchId: string;
      payload: UpdateMeterDispatchPayload;
    }) => mxApi.updateMeterDispatchDetail(dispatchId, payload)
  );
};
export const useDownloadTemplateOperations = () => {
  return useSmartMutation(
    (params: { remote_utility_id: any; run_id: string }) =>
      mxApi.downloadTemplateOperations(params),
    {
      onSuccess: (blob, variables) => {
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `template_operations_${variables.run_id}.csv`; // You can adjust the filename and extension
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
    }
  );
};

export const useCreateTemplateOperations = () => {
  return useSmartMutation((payload: CreateTemplateOperationsPayload) =>
    mxApi.createTemplateOperations(payload)
  );
};

export const useRunStatusChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meter.meterStatus(), // Unique query key for caching
    () => mxApi.getRunStatusChoices()
  );
};

export const useManualRunStatusChoices = () => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meter.manualRunStatus(), // Unique query key for caching
    () => mxApi.getManualRunStatusChoices()
  );
};

export const getDispatchStatus = () => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meter.dispatchStatus(), // Unique query key for caching
    () => mxApi.getDispatchStatus()
  );
};

export const useMeterReaderList = (remoteUtilityId) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meter.meterReaderList(),
    () => getMeterReaderList(remoteUtilityId),
    {
      staleTime: 0,
      enabled: !!remoteUtilityId,
      refetchOnMount: true,
    }
  );
};

export const useRunReadings = (
  remoteUtilityId: string,
  meterRunId: string,
  status?: string,
  type?: string,
  search?: string,
  page?: number
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meter.meterRunReadings({
      remoteUtilityId,
      meterRunId,
      status,
      type,
      search,
      page,
    }),
    () =>
      getReadingsList(remoteUtilityId, meterRunId, status, type, search, page),
    {
      enabled: !!remoteUtilityId && !!meterRunId,
    }
  );
};

export const useRunValidations = () => {
  return useSmartMutation(
    ({ payload }: { payload }) => runValidations(payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ["mx", "validation-break-run-count"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "view-readings-list"],
        });
        queryClient.refetchQueries({
          queryKey: ["mx", "missing-readings-list"],
        });
      },
    }
  );
};

export const useGetMarkValidationDetail = (remoteUtilityId, meterRunId) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.meter.meterValidationCompletion(meterRunId),
    () => getValidationCompletionDetail(remoteUtilityId, meterRunId),
    {
      staleTime: 0,
      enabled: !!meterRunId,
      refetchOnMount: true,
    }
  );
};

export const useMarkCycleAsComplete = () => {
  return useSmartMutation(({ payload }: { payload }) =>
    markCycleAsComplete(payload)
  );
};

export const usePhotoMeterGetRules = (
  ruleType: number,
  open: boolean,
  meterCycleId?: number
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.readCycleManagement.getPhotoMeterRules(
      ruleType,
      utilityId,
      meterCycleId
    ),
    () => getPhotoMeterRules(ruleType, utilityId, meterCycleId),
    {
      enabled: !!open || !!utilityId || !!ruleType,
    }
  );
};
